import { AxiosInstance } from 'axios'
import {
    User,
    UserCreate,
    UserUpdate,
    ListUsersParams,
    PaginatedUserResponse,
    ChangePasswordRequest,
} from './user.type'

export class UserApi {
    constructor(private readonly client: AxiosInstance) {}

    /**
     * Get all users with pagination
     */
    async list(params?: ListUsersParams): Promise<PaginatedUserResponse> {
        const response = await this.client.get<PaginatedUserResponse>('/users', {
            params,
        })
        return response.data
    }

    async listAll(params?: ListUsersParams): Promise<PaginatedUserResponse> {
        const response = await this.client.get<PaginatedUserResponse>('/users/list-all', {
            params,
        })
        return response.data
    }

    /**
     * Get a single user by ID
     */
    async findOne(id: string): Promise<User> {
        const response = await this.client.get<User>(`/users/${id}`)
        return response.data
    }

    /**
     * Create a new user
     */
    async create(payload: UserCreate): Promise<User> {
        const response = await this.client.post<User>('/users', payload)
        return response.data
    }

    /**
     * Update a user
     */
    async update(id: string, payload: UserUpdate): Promise<User> {
        const response = await this.client.put<User>(`/users/${id}`, payload)
        return response.data
    }

    /**
     * Update current user
     */
    async updateMe(payload: UserUpdate): Promise<User> {
        const response = await this.client.put<User>(`/users/me`, payload)
        return response.data
    }

    /**
     * Change current user password
     */
    async changePassword(payload: ChangePasswordRequest): Promise<void> {
        await this.client.put('/users/me/password', payload)
    }

    /**
     * Soft delete a user
     */
    async softDelete(id: string): Promise<void> {
        await this.client.delete(`/users/${id}/soft`)
    }

    /**
     * Delete a user (hard delete)
     */
    async delete(id: string): Promise<void> {
        await this.client.delete(`/users/${id}`)
    }

    /**
     * Update user avatar
     */
    async updateAvatar(file: File | string): Promise<User> {
        const formData = new FormData()
        formData.append('avatar', file)
        const response = await this.client.put<User>('/users/me/avatar', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        return response.data
    }

    /**
     * Re-invite a user (send invitation again)
     */
    async reInvite(id: string): Promise<User> {
        const response = await this.client.post<User>(`/users/${id}/re-invite`)
        return response.data
    }
}
